"use client";

import React, { useState, useEffect } from "react";

const formatWithCommas = (value: number | string | undefined): string =>
    typeof value === "number" || typeof value === "string"
        ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        : "";

type Props = {
    originalLoan: number;
    setOriginalLoan: (n: number) => void;
    originalTerm: number;
    setOriginalTerm: (n: number) => void;
    yearsPaid: number;
    setYearsPaid: (n: number) => void;
    interestRate: number;
    setInterestRate: (n: number) => void;
};

const CurrentLoan = ({
    originalLoan,
    setOriginalLoan,
    originalTerm,
    setOriginalTerm,
    yearsPaid,
    setYearsPaid,
    interestRate,
    setInterestRate,
}: Props) => {
    const [loanInput, setLoanInput] = useState("0");
    const [isOpen, setIsOpen] = useState(true);

    useEffect(() => {
        setLoanInput(formatWithCommas(originalLoan));
    }, [originalLoan]);

    return (
        <div className="border border-gray-300 overflow-hidden shadow 2xl:w-[900px]">
            <button
                type="button"
                className="w-full text-left bg-[#202358] text-white px-4 py-3 cursor-pointer select-none flex items-center justify-between hover:bg-blue active:bg-blue focus:bg-blue 2xl:h-[42px]"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="text-[18px] font-bold uppercase">Current LOAN</span>
                <span className="text-xl">{isOpen ? "−" : "+"}</span>
            </button>

            <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
                }`}
            >
                <div className="bg-white p-2 2xl:w-[898px]">
                    {/* Original Loan */}
                    <div className="flex flex-col gap-3 2xl:flex 2xl:flex-row 2xl:items-start 2xl:gap-2">
                        <div className="2xl:w-[367px] 2xl:flex 2xl:justify-end">
                            <label
                                id="originalLoan-label"
                                htmlFor="originalLoan"
                                className="w-32 font-medium text-black text-[15px] 2xl:w-[106px] content-center"
                            >
                                Original Loan: $
                            </label>
                            <input
                                id="originalLoan"
                                type="text"
                                inputMode="numeric"
                                className="border px-3 w-36 text-left 2xl:w-[122px] 2xl:h-[42px] bg-[#f6f9ff] text-[14px]"
                                value={loanInput}
                                onChange={(e) => {
                                    const raw = e.target.value.replace(/,/g, "");
                                    if (/^\d*$/.test(raw)) {
                                        const numeric = Number(raw);
                                        setOriginalLoan(numeric);
                                        setLoanInput(formatWithCommas(raw));
                                    }
                                }}
                                onBlur={() => {
                                    setLoanInput(formatWithCommas(originalLoan));
                                }}
                            />
                        </div>
                        <input
                            id="originalLoan-range"
                            aria-label="Original loan amount"
                            type="range"
                            min={0}
                            max={10000000}
                            step={1000}
                            value={originalLoan}
                            onChange={(e) => {
                                const val = Number(e.target.value);
                                setOriginalLoan(val);
                                setLoanInput(formatWithCommas(val));
                            }}
                            className="w-full 2xl:w-[506px] accent-blue"
                        />
                    </div>
                    <div className="flex justify-end text-xs text-black 2xl:-mt-1">
                        <div className="pb-10 justify-between w-full max-w-md 2xl:pb-0 flex 2xl:justify-between 2xl:w-full 2xl:max-w-md 2xl:text-[10px] 2xl:-mt-3.5 2xl:mb-2.5">
                            <span>$0</span>
                            <span>$100K</span>
                            <span>$1M</span>
                            <span>$10M</span>
                        </div>
                    </div>

                    {/* Original Term */}
                    <div className="flex flex-col gap-3 2xl:flex 2xl:flex-row 2xl:items-start 2xl:gap-2">
                        <div className="2xl:w-[367px] 2xl:flex 2xl:justify-end">
                            <label
                                id="originalTerm-label"
                                htmlFor="originalTerm"
                                className="w-32 font-medium text-black text-[15px] 2xl:w-[120px] content-center"
                            >
                                Original Term (Yr):
                            </label>
                            <input
                                id="originalTerm"
                                type="number"
                                className="border px-3 w-36 text-left 2xl:w-[122px] 2xl:h-[42px] bg-[#f6f9ff] text-[14px]"
                                value={originalTerm}
                                onChange={(e) => setOriginalTerm(Number(e.target.value))}
                            />
                        </div>
                        <input
                            id="originalTerm-range"
                            aria-label="Original loan term in years"
                            type="range"
                            min={0}
                            max={40}
                            step={1}
                            value={originalTerm}
                            onChange={(e) => setOriginalTerm(Number(e.target.value))}
                            className="w-full 2xl:w-[506px] accent-blue"
                        />
                    </div>
                    <div className="flex justify-end text-xs text-black 2xl:-mt-1">
                        <div className="pb-10 justify-between w-full max-w-md 2xl:pb-0 flex 2xl:justify-between 2xl:w-full 2xl:max-w-md 2xl:text-[10px] 2xl:-mt-3.5 2xl:mb-2.5">
                            <span>0</span>
                            <span>13</span>
                            <span>26</span>
                            <span>40</span>
                        </div>
                    </div>

                    {/* Years Paid */}
                    <div className="flex flex-col gap-3 2xl:flex 2xl:flex-row 2xl:items-start 2xl:gap-2">
                        <div className="2xl:w-[367px] 2xl:flex 2xl:justify-end">
                            <label
                                id="yearsPaid-label"
                                htmlFor="yearsPaid"
                                className="w-32 font-medium text-black text-[15px] 2xl:w-[75px] content-center"
                            >
                                Years Paid:
                            </label>
                            <input
                                id="yearsPaid"
                                type="number"
                                className="border px-3 w-36 text-left 2xl:w-[122px] 2xl:h-[42px] bg-[#f6f9ff] text-[14px]"
                                value={yearsPaid}
                                onChange={(e) => setYearsPaid(Number(e.target.value))}
                            />
                        </div>
                        <input
                            id="yearsPaid-range"
                            aria-label="Years paid"
                            type="range"
                            min={1}
                            max={originalTerm || 40}
                            step={1}
                            value={yearsPaid}
                            onChange={(e) => setYearsPaid(Number(e.target.value))}
                            className="w-full 2xl:w-[506px] accent-blue"
                        />
                    </div>

                    <div className="flex justify-end text-xs text-black 2xl:-mt-1">
                        <div className="pb-10 justify-between w-full max-w-md 2xl:pb-0 flex 2xl:justify-between 2xl:w-full 2xl:max-w-md 2xl:text-[10px] 2xl:-mt-3.5 2xl:mb-2.5">
                            <span>1</span>
                            <span>13</span>
                            <span>26</span>
                            <span>40</span>
                        </div>
                    </div>

                    {/* Interest Rate */}
                    <div className="flex flex-col gap-3 2xl:flex 2xl:flex-row 2xl:items-start 2xl:gap-2">
                        <div className="2xl:w-[367px] 2xl:flex 2xl:justify-end">
                            <label
                                id="interestRate-label"
                                htmlFor="interestRate"
                                className="w-32 font-medium text-black text-[15px] 2xl:w-[106px] content-center"
                            >
                                Interest Rate: %
                            </label>
                            <input
                                id="interestRate"
                                type="number"
                                className="border px-3 w-36 text-left 2xl:w-[122px] 2xl:h-[42px] bg-[#f6f9ff] text-[14px]"
                                value={interestRate}
                                onChange={(e) => setInterestRate(Number(e.target.value))}
                            />
                        </div>
                        <input
                            id="interestRate-range"
                            aria-label="Interest rate"
                            type="range"
                            min={0}
                            max={10}
                            step={0.1}
                            value={interestRate}
                            onChange={(e) => setInterestRate(Number(e.target.value))}
                            className="w-full 2xl:w-[506px] accent-blue"
                        />
                    </div>

                    <div className="flex justify-end text-xs text-black 2xl:-mt-1">
                        <div className="pb-10 justify-between w-full max-w-md 2xl:pb-0 flex 2xl:justify-between 2xl:w-full 2xl:max-w-md 2xl:text-[10px] 2xl:-mt-3.5 2xl:mb-2.5">
                            <span>0%</span>
                            <span>3.5%</span>
                            <span>6.5%</span>
                            <span>10%</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CurrentLoan;
